import "ol/ol.css";
import "./FilterMap.scss";

import * as olControl from "ol/control";
import * as olProj from "ol/proj";
import AsyncSelect from "react-select/async";

import React, { Component } from "react";
import {
  enableDragBoxInteraction,
  enableDrawInteraction,
  enableSelectInteraction,
} from "./mapInteractions";
import { getHeight, getWidth } from "ol/extent";
import {
  locationsStyleFunction,
  passlinesStyleFunction,
  selectionStyle,
} from "../../utils/mapStyles";

import { DashboardConfig } from "../../config/kyvDashboard";
import { LayerSwitcherMixin } from "../../chart-components/Mixins/LayerSwitcherMixin";
import PropTypes from "prop-types";
import olGeoJSON from "ol/format/GeoJSON";
import olMap from "ol/Map";
import olVectorLayer from "ol/layer/Vector";
import olVectorSource from "ol/source/Vector";
import olVectorSourceCluster from "ol/source/Cluster";
import olView from "ol/View";
import olWKT from "ol/format/WKT";
import { selectableLayers } from "../Map/selectableLayers";
import { DataLoader } from "../../ui-components/DataLoader/DataLoader";
import { ExecUtils } from "../../chart-components/ChartUtils/ExecUtils";

export class FilterMap extends Component {
  static propTypes = {
    // setMap: PropTypes.func.isRequired,
    setFilter: PropTypes.func.isRequired,
    filter: PropTypes.object.isRequired,
    passlines: PropTypes.arrayOf(PropTypes.object).isRequired,
    locations: PropTypes.arrayOf(PropTypes.object).isRequired,
    municipalities: PropTypes.arrayOf(PropTypes.object).isRequired,
    interactionType: PropTypes.oneOf([
      "drawPoint",
      "drawLineString",
      "drawPolygon",
      "drawBox",
      "selectPoint",
      "selectPolygon",
      "selectBox",
      "overlayPolygon",
    ]),
    selectionLayer: PropTypes.oneOf(["passlines", "locationIds", "locations"]),
    selectionGeometry: PropTypes.arrayOf(PropTypes.any),
    numGeom: PropTypes.number,
  };

  static defaultProps = {
    numGeom: 1,
  };

  mapRef = null;
  queryRef = null;

  geoJsonFmt = new olGeoJSON({
    dataProjection: "EPSG:4326",
    featureProjection: "EPSG:3857",
  });

  wktFmt = new olWKT({
    dataProjection: "EPSG:4326",
    featureProjection: "EPSG:3857",
  });

  drawingVectorSource = new olVectorSource();
  drawingLayer = null;
  passlinesVectorSource = new olVectorSource();
  passlinesLayer = null;
  locationsVectorSource = new olVectorSource();
  locationsLayer = null;

  map = null;

  interactions = [];

  constructor(props) {
    super(props);

    this.state = {
      locations: props.locations,
      municipalities: props.municipalities,
      passlines: props.passlines,
      filter: props.filter,
      numGeom: props.numGeom,
    };

    this.mapRef = React.createRef();
    this.queryRef = React.createRef();

    // Bind functions to scope
    this.initializeMap = this.initializeMap.bind(this);
    this.renderDrawingLayer = this.renderDrawingLayer.bind(this);
    this.populateLayers = this.populateLayers.bind(this);
    this.getInteractionConfig = this.getInteractionConfig.bind(this);
    this.displayLayers = this.displayLayers.bind(this);
  }

  componentDidMount() {
    if (!this.map) {
      this.initializeMap();
      this.populateLayers();
      this.renderDrawingLayer();
      this.displayLayers();
    }
  }

  componentDidUpdate(prevProps, props) {
    this.populateLayers();
    this.renderDrawingLayer();
    this.activateInteraction();
    this.displayLayers();
  }

  displayLayers() {
    const { selectionLayer } = this.props;
    this.passlinesLayer.setVisible(false);
    this.locationsLayer.setVisible(false);

    this.drawingLayer.setVisible(true);

    switch (selectionLayer) {
      case selectableLayers.passlines:
        this.passlinesLayer.setVisible(true);
        break;
      case selectableLayers.locationIds:
      case selectableLayers.location:
        this.locationsLayer.setVisible(true);
        break;
      default:
    }
  }

  /**
   * Parse SSR results into options
   * @param {*} response
   * @returns
   */
  parseSsrOptions(response) {
    return [
      {
        label: "Stedsnavn",
        options: response.navn.map((navn) => {
          return {
            value: navn,
            label: (
              <>
                <strong>{navn["skrivemåte"]}</strong>
                {" - "}
                <em>
                  {navn.navneobjekttype}
                  {" - "}({navn.kommuner[0].kommunenavn},{" "}
                  {navn.fylker[0].fylkesnavn})
                </em>
              </>
            ),
          };
        }),
      },
    ];
  }

  /**
   * Search for names in SSR
   * @param {*} queryTerm
   * @param {*} setOptions
   */
  searchSsr(queryTerm, setOptions) {
    if (queryTerm && queryTerm.trim().length > 3) {
      DataLoader.query_ssr(queryTerm).then((res) => {
        if (res && Array.isArray(res.navn) && res.navn.length > 0) {
          setOptions(this.parseSsrOptions(res));
        }
      });
    }
  }

  activateInteraction() {
    const {
      interactionType,
      selectionLayer,
      selectionGeometry,
      setFilter,
      numGeom,
    } = this.props;

    const { layer, featureProperty, filterKey } =
      this.getInteractionConfig(selectionLayer);

    switch (interactionType) {
      case "overlayPolygon":
        this.overlayPolygon(
          setFilter,
          selectionGeometry,
          layer,
          featureProperty,
          filterKey
        );
        break;
      case DashboardConfig.filterControls.drawPoint:
        enableDrawInteraction(
          this.map,
          this.drawingLayer,
          setFilter,
          "Point",
          null,
          null,
          null,
          numGeom
        );
        break;
      case DashboardConfig.filterControls.drawLine:
        enableDrawInteraction(
          this.map,
          this.drawingLayer,
          setFilter,
          "LineString",
          null,
          null,
          null,
          numGeom
        );
        break;
      case DashboardConfig.filterControls.drawPolygon:
        enableDrawInteraction(
          this.map,
          this.drawingLayer,
          setFilter,
          "Polygon"
        );
        break;
      case DashboardConfig.filterControls.drawBox:
        enableDragBoxInteraction(this.map, this.drawingLayer, setFilter);
        break;
      case DashboardConfig.filterControls.selectPoint:
        enableSelectInteraction(
          this.map,
          this.drawingLayer,
          setFilter,
          layer,
          featureProperty,
          filterKey
        );
        break;
      case DashboardConfig.filterControls.selectPolygon:
        enableDrawInteraction(
          this.map,
          this.drawingLayer,
          setFilter,
          "Polygon",
          layer,
          featureProperty,
          filterKey
        );
        break;
      case DashboardConfig.filterControls.selectBox:
        enableDragBoxInteraction(
          this.map,
          this.drawingLayer,
          setFilter,
          layer,
          featureProperty,
          filterKey
        );
        break;
      default:
        if (interactionType) {
          console.error("No valid interaction specified", interactionType);
        }
    }
  }

  initializeMap() {
    var elem = document.createElement("div");
    elem.innerText = "Query control here";

    var ssrQueryCtrl = new olControl.Control({
      element: this.queryRef.current,
    });

    var zoomPt = [15, 61];
    this.map = new olMap({
      pixelRatio: 1,
      target: this.mapRef.current,
      featureProjection: "EPSG:3857",
      controls: olControl
        .defaults({
          attributionOptions: {
            collapsible: false,
          },
        })
        .extend([ssrQueryCtrl]),
      view: new olView({
        center: olProj.transform(
          [zoomPt[0], zoomPt[1]],
          "EPSG:4326",
          "EPSG:3857"
        ),
        zoom: 4,
      }),
    });

    // Add base layers and layer switcher
    LayerSwitcherMixin(this.map);

    // Add vector layers
    this.passlinesLayer = new olVectorLayer({
      title: "Passlines",
      visible: false,
      source: this.passlinesVectorSource,
      zIndex: 1000,
      style: passlinesStyleFunction,
    });
    this.map.addLayer(this.passlinesLayer);

    this.locationsLayer = new olVectorLayer({
      title: "Locations",
      visible: false,
      source: new olVectorSourceCluster({
        distance: 25,
        source: this.locationsVectorSource,
      }),
      zIndex: 1100,
      style: locationsStyleFunction,
      declutter: true,
    });
    this.map.addLayer(this.locationsLayer);

    this.drawingLayer = new olVectorLayer({
      title: "Selection",
      visible: true,
      source: this.drawingVectorSource,
      zIndex: 1900,
      style: () => {
        return selectionStyle;
      },
    });

    this.map.addLayer(this.drawingLayer);
  }

  overlayPolygon(
    setFilter,
    selectionGeometry,
    layer,
    featureProperty,
    filterKey
  ) {
    var selGeoms = selectionGeometry.map((geom) =>
      this.geoJsonFmt.readFeature(geom).getGeometry()
    );

    var olVecSrc;

    if (
      layer.getSource() &&
      typeof layer.getSource()["getFeatures"] === "function"
    ) {
      olVecSrc = layer.getSource().getSource();
    } else {
      olVecSrc = layer.getSource();
    }

    var filteredFeatures = [];

    selGeoms.forEach((selGeom) => {
      olVecSrc.forEachFeatureInExtent(selGeom.getExtent(), (ifeat) => {
        if (selGeom.intersectsExtent(ifeat.getGeometry().getExtent())) {
          filteredFeatures.push(ifeat);
        }
      });
    });

    var updatedFilter = {};
    if (featureProperty === "geometry") {
      updatedFilter[filterKey] = filteredFeatures.map((ft) =>
        this.geoJsonFmt.writeGeometryObject(ft.getGeometry())
      );
    } else {
      updatedFilter[filterKey] = filteredFeatures.map(
        (ft) => ft.getProperties()[featureProperty]
      );
    }

    setFilter(updatedFilter, true);
  }

  populateLayers() {
    const { passlines, locations } = this.props;

    if (
      Array.isArray(locations) &&
      this.locationsVectorSource.getFeatures().length !== locations.length
    ) {
      this.locationsVectorSource.clear();
      this.locationsVectorSource.addFeatures(
        locations.map((l) => this.geoJsonFmt.readFeature(l))
      );
    }

    if (
      Array.isArray(passlines) &&
      this.passlinesVectorSource.getFeatures().length !== passlines.length
    ) {
      this.passlinesVectorSource.clear();
      this.passlinesVectorSource.addFeatures(
        passlines.map((p) => this.geoJsonFmt.readFeature(p))
      );
    }
  }

  getInteractionConfig(layerName) {
    switch (layerName) {
      case "locationIds":
        return {
          layer: this.locationsLayer,
          featureProperty: "id",
          filterKey: "locationIds",
        };
      case "locations":
        return {
          layer: this.locationsLayer,
          featureProperty: "geometry",
          filterKey: "geom",
        };
      case "passlines":
        return {
          layer: this.passlinesLayer,
          featureProperty: "geometry",
          filterKey: "geom",
        };
      default:
        return {};
    }
  }

  renderDrawingLayer() {
    const { filter, numGeom } = this.props;
    if (this.map && filter) {
      try {
        if (!(this.drawingVectorSource.getFeatures().length < numGeom)) {
          this.drawingVectorSource.clear();
        }
        if (Array.isArray(filter.geom)) {
          var olFeatures = filter.geom.map((wktStr) => {
            return this.wktFmt.readFeature(wktStr, {
              dataProjection: "EPSG:4326",
              featureProjection: "EPSG:3857",
            });
          });
          this.drawingVectorSource.addFeatures(olFeatures);
          if (this.drawingVectorSource.getFeatures().length > 0) {
            var extent = this.drawingVectorSource.getExtent();
            if (extent && (getWidth(extent) > 0 || getHeight(extent) > 0)) {
              this.map.getView().fit(this.drawingVectorSource.getExtent());
              this.map.getView().setZoom(this.map.getView().getZoom() - 2);
            }
          }
        }
      } catch (error) {
        console.error(error);
      }
    }
  }

  render() {
    return (
      <>
        <div className="filter--map" ref={this.mapRef} />
        <div className="filter--map--ssr-search" ref={this.queryRef}>
          <AsyncSelect
            cacheOptions
            placeholder={"Stedsnavn"}
            loadingMessage={(d) => "Søker"}
            loadOptions={ExecUtils.debounceTwoArgs(this, this.searchSsr, 500)}
            defaultOptions
            isMulti={false}
            onChange={(v) => {
              const { representasjonspunkt, navneobjekttype } = v.value;

              let zoomLevel;

              switch (navneobjekttype) {
                case "Fylke":
                  zoomLevel = 8;
                  break;
                case "Kommune":
                case "Fjord":
                  zoomLevel = 12;
                  break;
                case "By":
                case "Tettsted":
                  zoomLevel = 14;
                  break;
                default:
                  zoomLevel = 16;
              }

              const centerPoint = olProj.transform(
                [representasjonspunkt["øst"], representasjonspunkt["nord"]],
                "EPSG:4326",
                "EPSG:3857"
              );
              this.map
                .getView()
                .animate({ zoom: zoomLevel, center: centerPoint });
            }}
          />
        </div>
      </>
    );
  }
}
